import ErrorIcon from '@mui/icons-material/Error'
import {
	Box,
	Chip,
	ChipPropsVariantOverrides,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Tabs,
	Tab,
} from '@mui/material'
import * as React from 'react'
import { WorkItemInfo } from '../../types/Work/WorkItemInfo'
import CloseIcon from '@mui/icons-material/Close'
import { getWorkItemInfo } from '../../services/backlogService'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { getWorkItemProgress } from '../../services/workItemService'
import { ActualHoursRecords, ProgressRecord } from '../../types/Work/ProgressRecord'
import { Line } from 'react-chartjs-2'
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	ChartOptions,
} from 'chart.js'
import { WorkItem } from '../../types/Work/WorkItem'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

type InfoDialogProps = {
	dialogOnly?: boolean
	onClose?: () => void
	workItem: WorkItem
}

interface TabPanelProps {
	children?: React.ReactNode
	index: number
	value: number
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props

	return (
		<div
			role='tabpanel'
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</div>
	)
}

export const OpenInfoDialogButton = ({
	workItem,
	dialogOnly = false,
	onClose,
}: InfoDialogProps) => {
	const [open, setOpen] = React.useState(dialogOnly)
	const [workItemInfo, setWorkItemInfo] = React.useState<WorkItemInfo>()
	const [loading, setLoading] = React.useState<boolean>(false)
	const [progress, setProgress] = React.useState<ProgressRecord[]>([])
	const [actualHours, setActualHours] = React.useState<ActualHoursRecords[]>([])
	const [tabValue, setTabValue] = React.useState(0)

	const loadData = async () => {
		setLoading(true)
		const info = await getWorkItemInfo([workItem.id], null)
		const analysis = await getWorkItemProgress(workItem.id)
		setWorkItemInfo(info[0])
		setProgress(analysis.progressRecords)
		setActualHours(analysis.actualHoursRecords)
		setLoading(false)
	}

	React.useEffect(() => {
		if (dialogOnly) loadData().catch(console.error)
	}, [dialogOnly])

	const handleClickOpen = () => {
		setOpen(true)
		loadData().catch(console.error)
	}

	const handleClose = () => {
		setOpen(false)
		onClose?.()
	}

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue)
	}

	const getLayerVariant = (index: number): keyof ChipPropsVariantOverrides => {
		return `Layer${index}${
			workItem.outOfScope ? 'OutOfScope' : 'WorkItem'
		}` as keyof ChipPropsVariantOverrides
	}

	function handleCopy(workItemName: string): void {
		navigator.clipboard.writeText(workItemName)
	}
	function getForecastedHours() {
		const remainingHours = workItem.budgetedHours - workItem.burnedHours

		const forecastHours = remainingHours / workItem.pf + workItem.burnedHours
		return forecastHours
	}

	const cumulativeChartData = {
		labels: progress.map((record) => new Date(record.reportedAt).toLocaleDateString()),
		datasets: [
			{
				label: 'Cumulative Quantity Earned',
				data: progress.reduce((acc: number[], record, index) => {
					const previousTotal = index === 0 ? 0 : acc[index - 1]
					acc.push(previousTotal + record.dayQuantityEarned)
					return acc
				}, []),
				borderColor: 'rgb(75, 192, 192)',
				tension: 0.1,
			},
		],
	}

	const earnedValueChartData = {
		labels: [
			...actualHours.map((record) => new Date(record.reportedAt).toLocaleDateString()),
			'unknown',
		],
		datasets: [
			{
				label: `Earned Value (${workItem.pf} PF)`,
				data: (() => {
					return [
						...actualHours.reduce((acc: number[], record, index) => {
							const previousTotal = index === 0 ? 0 : acc[index - 1]
							acc.push(previousTotal + record.dayActualHours)
							return acc
						}, []),
						getForecastedHours(),
					]
				})(),
				borderColor: 'rgb(255, 99, 132)',
				tension: 0.1,
				segment: {
					borderDash: (ctx: { p1DataIndex: number }) =>
						ctx.p1DataIndex === actualHours.length ? [5, 5] : undefined,
				},
			},
			{
				label: 'Budgeted Hours',
				data: Array(actualHours.length + 1).fill(workItem.budgetedHours),
				borderColor: 'rgb(54, 162, 235)',
				borderWidth: 3,
				borderDash: [2, 2],
				pointRadius: 0,
				tension: 0,
			},
		],
	}

	const chartOptions: ChartOptions<'line'> = {
		responsive: true,
		interaction: {
			mode: 'nearest',
			intersect: false,
		},
		plugins: {
			legend: {
				position: 'top',
			},
			title: {
				display: false,
			},
			tooltip: {
				callbacks: {
					label: (context: any) => {
						const index = context.dataIndex
						if (tabValue === 1) {
							// Quantity tab
							return [
								`Cumulative Quantity Earned: ${context.parsed.y.toFixed(2)}`,
								`Day's Quantity Earned: ${progress[index].dayQuantityEarned}`,
							]
						} else if (tabValue === 2) {
							// Hours tab
							if (context.datasetIndex === 1) {
								return `Budgeted Hours: ${context.parsed.y.toFixed(2)}`
							}
							if (index === actualHours.length) {
								const lastActualPoint = actualHours.reduce(
									(sum, record) => sum + record.dayActualHours,
									0,
								)
								return [
									`Forecasted Completion: ${context.parsed.y.toFixed(2)} hours`,
									`Forecasted Remaining Actual Hours: ${(
										context.parsed.y - lastActualPoint
									).toFixed(2)}`,
								]
							}
							return [
								`Cumulative Actual Hours: ${context.parsed.y.toFixed(2)}`,
								`Day's Actual Hours: ${actualHours[index].dayActualHours}`,
							]
						}
						return []
					},
				},
			},
		},
		scales: {
			y: {
				beginAtZero: true,
				max:
					tabValue === 1
						? workItem.quantityBudgeted
						: Math.max(
								workItem.budgetedHours + 10,
								workItem.burnedHours + 10,
								getForecastedHours() * 1.1,
						  ),
				title: {
					display: true,
					text: tabValue === 1 ? 'Quantity' : 'Hours',
				},
			},
			x: {
				title: {
					display: true,
					text: 'Date',
				},
			},
		},
	}

	return (
		<>
			{!dialogOnly && (
				<IconButton onClick={handleClickOpen} title='View Work Item Info'>
					<ErrorIcon color='primary' />
				</IconButton>
			)}

			<Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
				{loading ? (
					<CircularProgress color='success' />
				) : (
					<>
						<DialogTitle>{workItemInfo?.workItemName} Info</DialogTitle>
						<IconButton
							onClick={handleClose}
							sx={(theme) => ({
								position: 'absolute',
								right: 8,
								top: 8,
								color: theme.palette.grey[500],
							})}
						>
							<CloseIcon />
						</IconButton>
						<DialogContent>
							<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
								<Tabs value={tabValue} onChange={handleTabChange}>
									<Tab label='Hierarchy' />
									<Tab label='Quantity' />
									<Tab label='Hours' />
								</Tabs>
							</Box>
							<TabPanel value={tabValue} index={0}>
								<Table sx={{ width: '100%' }}>
									<TableBody>
										{workItemInfo?.hierarchy.map((hWorkItem, index) => (
											<TableRow key={index}>
												<TableCell component='th' scope='row'>
													<Chip label={ hWorkItem.alias} variant={getLayerVariant(hWorkItem.index)} />
												</TableCell>
												<TableCell align='right' sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
													{hWorkItem.name}
													<IconButton onClick={(e) => handleCopy(hWorkItem.name)} color='primary'>
														<ContentCopyIcon />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TabPanel>
							<TabPanel value={tabValue} index={1}>
								<Box sx={{ height: 400 }}>
									<Line options={chartOptions} data={cumulativeChartData} />
								</Box>
							</TabPanel>
							<TabPanel value={tabValue} index={2}>
								<Box sx={{ height: 400 }}>
									<Line options={chartOptions} data={earnedValueChartData} />
								</Box>
							</TabPanel>
						</DialogContent>
					</>
				)}
			</Dialog>
		</>
	)
}
